import React, { useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Inbox, Loader2, MoreHorizontal, Pencil } from "lucide-react";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import BottomSheet from "./bottom-sheet";
import ActionSheet from "./action-sheet";
import { cn, getBadgeVariant } from "../../lib/utils";
import type {
  DataTableAction,
  DataTableColumn,
  EnabledToggleConfig,
  TableRowData,
} from "../data-table";

export interface MobilePagerState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  showingStart: number;
  showingEnd: number;
  onPrevious: () => void;
  onNext: () => void;
}

interface MobileDataListProps {
  columns: DataTableColumn[];
  rows: TableRowData[];
  actions?: DataTableAction[];
  loading: boolean;
  enabledToggle?: EnabledToggleConfig;
  pager: MobilePagerState;
  getValue: (row: TableRowData, accessor: string) => any;
  formatValue: (value: any, format?: "number") => string;
  /** Resolves false when the edit failed and the editor should stay open. */
  onEditCell: (row: TableRowData, index: number, column: DataTableColumn, newValue: string) => Promise<boolean>;
}

const SPECIAL_ACCESSORS = new Set(["icon_url", "status", "enabled"]);
const VISIBLE_META_FIELDS = 4;
const EMPTY = "---";

interface EditTarget {
  row: TableRowData;
  index: number;
  column: DataTableColumn;
  value: string;
}

const MobileDataList: React.FC<MobileDataListProps> = ({
  columns,
  rows,
  actions,
  loading,
  enabledToggle,
  pager,
  getValue,
  formatValue,
  onEditCell,
}) => {
  const [actionRow, setActionRow] = useState<TableRowData | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const contentColumns = columns.filter((column) => !SPECIAL_ACCESSORS.has(column.accessor));
  const [titleColumn, subtitleColumn, ...baseMetaColumns] = contentColumns;
  const headColumns = [titleColumn, subtitleColumn].filter(
    (column): column is DataTableColumn => !!column,
  );

  const displayOf = (row: TableRowData, column: DataTableColumn) =>
    formatValue(getValue(row, column.accessor), column.format);

  /** Title/subtitle come from the first two columns that have a value; empty editable ones stay reachable in the grid. */
  const layoutFor = (row: TableRowData) => {
    const filled = headColumns.filter((column) => displayOf(row, column) !== EMPTY);
    const emptyEditable = headColumns.filter(
      (column) => column.editable && !filled.includes(column),
    );
    return {
      title: filled[0],
      subtitle: filled[1],
      meta: [...emptyEditable, ...baseMetaColumns],
    };
  };

  const rowTitle = (row: TableRowData) => {
    const { title } = layoutFor(row);
    return title ? displayOf(row, title).split("\n")[0] : undefined;
  };
  const imageColumn = columns.find((column) => column.accessor === "icon_url");
  const statusColumn = columns.find((column) => column.accessor === "status");
  const enabledColumn = enabledToggle
    ? columns.find((column) => column.accessor === "enabled")
    : undefined;

  const visibleActionsFor = (row: TableRowData) =>
    (actions ?? []).filter((action) => !action.visible || action.visible(row));

  const rowKey = (row: TableRowData, index: number) => String(row.id ?? index);

  const toggleExpanded = (key: string) =>
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const openEditor = (row: TableRowData, index: number, column: DataTableColumn) => {
    const raw = getValue(row, column.accessor);
    const value =
      raw === undefined || raw === null || String(raw).trim().toLowerCase() === "undefined"
        ? ""
        : String(raw);
    setEditTarget({ row, index, column, value });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setIsSaving(true);
    try {
      const saved = await onEditCell(editTarget.row, editTarget.index, editTarget.column, editTarget.value);
      if (saved) setEditTarget(null);
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (row: TableRowData, index: number, column: DataTableColumn) => {
    const display = formatValue(getValue(row, column.accessor), column.format);
    return (
      <div key={column.accessor} className="min-w-0">
        <p className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {column.header}
        </p>
        {column.editable ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              openEditor(row, index, column);
            }}
            className="mt-0.5 flex max-w-full items-center gap-1.5 rounded-md border-0 bg-primary-50/60 px-1.5 py-0.5 text-left text-[13px] font-medium text-gray-900 active:bg-primary-100"
          >
            <span className="truncate">{display}</span>
            <Pencil className="h-3 w-3 shrink-0 text-primary-600" />
          </button>
        ) : (
          <p className="mt-0.5 whitespace-pre-line break-words text-[13px] font-medium text-gray-900 line-clamp-3">
            {display}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="relative space-y-3">
      {loading && (
        <div className="absolute inset-x-0 top-2 z-10 flex justify-center" aria-hidden="true">
          <span className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-md ring-1 ring-gray-100">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-600" />
            Loading
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50">
            <Inbox className="h-6 w-6 text-gray-400" />
          </span>
          <p className="mt-3 text-sm font-semibold text-gray-900">
            {loading ? "Loading…" : "Nothing here yet"}
          </p>
          {!loading && (
            <p className="mt-1 text-xs text-gray-500">Try a different search or filter.</p>
          )}
        </div>
      ) : (
        <ul className={cn("space-y-2.5 transition-opacity", loading && "opacity-60")}>
          {rows.map((row, index) => {
            const key = rowKey(row, index);
            const rowActions = visibleActionsFor(row);
            const primaryAction = actions?.[0];
            const isExpanded = expandedRows.has(key);
            const layout = layoutFor(row);
            const metaColumns = layout.meta;
            const shownMeta = isExpanded ? metaColumns : metaColumns.slice(0, VISIBLE_META_FIELDS);
            const hiddenCount = metaColumns.length - VISIBLE_META_FIELDS;
            const imageUrl = imageColumn ? String(row[imageColumn.accessor] ?? "").trim() : "";
            const status = statusColumn ? row[statusColumn.accessor] : undefined;
            const title = layout.title ? displayOf(row, layout.title) : EMPTY;
            const subtitle = layout.subtitle ? displayOf(row, layout.subtitle) : "";
            const rowId = String(row.id ?? "");

            const editButton = (column: DataTableColumn | undefined) =>
              column?.editable ? (
                <button
                  type="button"
                  aria-label={`Edit ${column.header}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    openEditor(row, index, column);
                  }}
                  className="-my-1 ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full border-0 bg-primary-50 p-0 align-middle text-primary-600 active:bg-primary-100"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              ) : null;

            return (
              <li key={key}>
                <div
                  role={primaryAction ? "button" : undefined}
                  tabIndex={primaryAction ? 0 : undefined}
                  onClick={() => primaryAction?.onClick(row)}
                  className={cn(
                    "rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(13,14,19,0.04)] [-webkit-tap-highlight-color:transparent]",
                    primaryAction && "transition-transform active:scale-[0.99] active:bg-gray-50/60",
                  )}
                >
                  <div className="flex items-start gap-3">
                    {imageColumn && (
                      imageUrl ? (
                        <img
                          src={imageUrl}
                          alt=""
                          className="h-12 w-12 shrink-0 rounded-xl border border-gray-100 bg-gray-50 object-cover"
                        />
                      ) : (
                        <span className="h-12 w-12 shrink-0 rounded-xl bg-gray-100" />
                      )
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 whitespace-pre-line break-words text-[15px] font-semibold leading-snug text-gray-900 line-clamp-3">
                          {title}
                          {editButton(layout.title)}
                        </p>
                        {status !== undefined && status !== null && status !== "" && (
                          <Badge
                            variant={getBadgeVariant(String(status))}
                            className="shrink-0 rounded-full text-[11px]"
                          >
                            {formatValue(status)}
                          </Badge>
                        )}
                      </div>
                      {subtitle && (
                        <p className="mt-0.5 whitespace-pre-line break-words text-[13px] text-gray-500 line-clamp-2">
                          {subtitle}
                          {editButton(layout.subtitle)}
                        </p>
                      )}
                    </div>
                    {rowActions.length > 0 && (
                      <button
                        type="button"
                        aria-label="Actions"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActionRow(row);
                        }}
                        className="-mr-2 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-400 active:bg-gray-100"
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  {shownMeta.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-gray-50 pt-3">
                      {shownMeta.map((column) => renderField(row, index, column))}
                    </div>
                  )}

                  {(hiddenCount > 0 || enabledColumn) && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      {hiddenCount > 0 ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpanded(key);
                          }}
                          className="flex items-center gap-1 border-0 bg-transparent p-0 text-xs font-semibold text-primary-600 active:opacity-60"
                        >
                          {isExpanded ? "Show less" : `Show ${hiddenCount} more`}
                          <ChevronDown
                            className={cn("h-3.5 w-3.5 transition-transform", isExpanded && "rotate-180")}
                          />
                        </button>
                      ) : (
                        <span />
                      )}
                      {enabledColumn && enabledToggle && (
                        <label
                          className="flex items-center gap-2 text-xs font-medium text-gray-600"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {enabledColumn.header}
                          <Switch
                            checked={Boolean(getValue(row, enabledColumn.accessor))}
                            disabled={enabledToggle.isToggling?.(rowId) ?? false}
                            onCheckedChange={(checked) => {
                              void enabledToggle.onToggle(row, checked);
                            }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pager.totalItems > 0 && (
        <div className="flex items-center justify-between gap-3 px-1 pt-1">
          <p className="text-xs text-gray-500">
            {pager.showingStart.toLocaleString()}–{pager.showingEnd.toLocaleString()} of{" "}
            {pager.totalItems.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous page"
              onClick={pager.onPrevious}
              disabled={pager.currentPage <= 1 || loading}
              className="flex h-10 w-10 items-center justify-center rounded-full border-0 bg-white p-0 text-gray-700 shadow-sm ring-1 ring-gray-100 active:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[3.5rem] text-center text-xs font-semibold text-gray-700">
              {pager.currentPage} / {pager.totalPages}
            </span>
            <button
              type="button"
              aria-label="Next page"
              onClick={pager.onNext}
              disabled={pager.currentPage >= pager.totalPages || loading}
              className="flex h-10 w-10 items-center justify-center rounded-full border-0 bg-white p-0 text-gray-700 shadow-sm ring-1 ring-gray-100 active:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <ActionSheet
        open={!!actionRow}
        onClose={() => setActionRow(null)}
        title={
          (actionRow && rowTitle(actionRow)) || "Actions"
        }
        actions={
          actionRow
            ? visibleActionsFor(actionRow).map((action) => ({
                label: action.label,
                icon: action.icon,
                onClick: () => action.onClick(actionRow),
              }))
            : []
        }
      />

      <BottomSheet
        open={!!editTarget}
        onClose={() => !isSaving && setEditTarget(null)}
        title={editTarget ? `Edit ${editTarget.column.header}` : undefined}
        description={
          editTarget ? rowTitle(editTarget.row) : undefined
        }
        footer={
          <button
            type="button"
            disabled={isSaving}
            onClick={() => void saveEdit()}
            className="h-12 w-full rounded-2xl border-0 bg-primary-600 text-sm font-semibold text-white active:bg-primary-700 disabled:opacity-60"
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
        }
      >
        {editTarget && (
          <div className="px-5 pb-4">
            <input
              autoFocus
              value={editTarget.value}
              inputMode={
                editTarget.column.editor === "text" || editTarget.column.format !== "number"
                  ? "text"
                  : "decimal"
              }
              onChange={(event) => setEditTarget({ ...editTarget, value: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === "Enter") void saveEdit();
              }}
              className="h-12 w-full rounded-xl border-0 bg-gray-50 px-4 text-base text-gray-900 ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}
      </BottomSheet>
    </div>
  );
};

export default MobileDataList;
